import { lazy, Suspense, useEffect } from 'react'
import type { ReactNode } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { registerNativeReturnHandler } from './lib/externalLink'
import { withCurrentSearch } from './lib/redirectSearch'
import { isPublicAddress } from './lib/publicAddress'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { AuthProvider } from './providers/AuthProvider'
import { ThemeProvider } from './providers/ThemeProvider'
import { AiJobsProvider } from './providers/AiJobsProvider'
import { VoiceDialerProvider } from './components/voice/VoiceDialerProvider'
// Phase 56.5.1: feature flags for deferred channels
// (WhatsApp / Messenger / Instagram). Default-off; flip the
// VITE_FEATURE_* env var in Vercel + redeploy to light up nav,
// routes, and channel pills for each channel. Page imports
// (InboxWhatsApp, InboxMessenger, InboxInstagram, AdminWhatsApp-
// Settings, AdminMetaConnections) stay above this line so a one-
// line env-var flip is the entire activation.
import { features } from './lib/features'
import { AuthGuard } from './components/AuthGuard'
import { VerifiedOnly } from './components/VerifiedOnly'
// Phase O2: niche-aware setup wizard. Mounts as a layout
// route at /onboarding/<step> separate from AppShell. Each
// step routes to the next via the registry order; the final
// step (tour) lands on /dashboard.
//
// Phase L1: removed legacy CategoryStep / BusinessSetup /
// NichePicker / TemplatePreviewStep / PhotoUpload pages and the
// OnboardingGate (numeric-step gating). The 5 paywall pages
// (/paywall/pro, /starter, /pro-trial, /starter-trial, /upgrade)
// were also removed along with their ProfileGuard wrappers; the
// new O1+O2 wizard owns the entire signup-to-onboarding flow,
// and post-signup redirects (Login, Signup, SignupAccount,
// Landing) now point to /onboarding/welcome directly.
// Phase O4: VA dashboard. /onboarding-queue lives outside
// AppShell so VAs (who are not bound to a sub_account) get a
// dedicated layout with SpecialistNav. The roster lives
// inside AppShell because it is platform admin chrome.
// Phase O6: admin UI for inspecting + toggling nurture sequences.
// Phase 62.C.2.10: admin cost dictionary CRUD.
// Phase 62.E.6: admin activation funnel dashboard.
// Phase 44: email campaign builder.
// Phase 40: demo account admin.
// Phase U16: web chat widget settings (owner/admin) +
// public-facing chat panel served at /widget/:sub_slug.
// Phase U17.2: WhatsApp inbox tab + admin settings.
// Phase U14.2: FB Messenger inbox tab + admin Meta connections.
// Wave 18.3: Meta Lead Ads connection page (feature-flagged).
// Wave 19: Google Ads Lead Forms connection page (feature-flagged).
// Phase U15: Instagram DM inbox tab.
// Phase 39.4.a: affiliate payouts settings (Whop hosted payouts portal).
// Phase 47: Reputation review automation.
// Phase V1.6: volume-tier graduation surface — current tier, MTD
// progress, distance to next, history, FAQ.
// Phase O5a: VA Google Calendar OAuth + availability rules.
// Self-serve at /dashboard/account/calendar; owner+admin view
// at /dashboard/team/:memberId/calendar.
// Wave 19A: per-sub_account notification preferences (new-lead email toggle).
// Phase 62.J: Ghost settings page + global floating widget.
// Wave 12: CRM Migration Assistant — import customers from HCP / Jobber / GHL / generic CSV.
// Wave 13: Branding + Business Hours + Service Area settings pages.
// Wave 13: real Payouts page (replaces PlaceholderPage).
// Phase 62.I: Reactivation list page (Ghost's first real surface).
// Wave 10: real calendar + tasks pages replacing the calendar
// PlaceholderPage. /dashboard/jobs (card list) stays as a parallel view.
// Phase 39.2: affiliate PDF statements.
// Phase F10.0.a: field operations dispatch board.
// Phase F10.0.b: tech-side schedule view.
import { AdminGuard } from './components/auth/AdminGuard'
// Phase 57.2: new dashboard Home page (replaces DashboardRootRedirect
// on the /dashboard route). The redirect function definition stays in
// this file for now as dead code; Batch 3 removes it.
// Wave 4 Lane 3: multi-business portfolio landing.
// Phase A2: section landing pages were removed. The 8 sections
// route the user straight to their first child via the
// DashboardRootRedirect below; PlaceholderPage stays for the
// five sub-pages that have not shipped yet.
const PlaceholderPage = lazy(() =>
  import('./components/dashboard/PlaceholderPage').then((m) => ({ default: m.PlaceholderPage })),
)
// Wave 13: Customer Portal — token-gated public view of a customer's
// bookings, invoices, payment history. White-labeled per sub_account.
// Wave 8.2: bare iframe target for the editor's right pane.
// Phase 62.A.S4: unified slug/username resolver for AI sites + legacy profiles.
// Phase 62.A.S4: AI site builder dashboard.
// Wave 8.1: SitesIndex (linear list) + NewSite (3-step stepper) replaced by
// SitesHome (Lovable-style greeting + prompt + grid). The old files are
// kept on disk for now (no imports) so a quick `git revert` works if needed,
// but Wave 8.2 deletes them.
// Phase O5c: tokenized public reschedule + cancel for onboarding calls.
// No AuthGuard — anon access gated by per-row token via SECURITY DEFINER RPCs.
// Phase 62.D.4: agency shell pages.
// Wave 2A: separate Affiliate Agreement at /affiliate-agreement (referenced
// in Terms Section 18 + footer copy). Public, no AuthGuard.
// Wave 2B: public affiliate program landing — drives signups for the
// R1-R6.2 affiliate program. Public, no AuthGuard.
// Phase 56.5: branded 404 (NotFound) wildcard route + top-level
// ErrorBoundary that renders ServerError on uncaught render errors.
import { ErrorBoundary } from './components/ErrorBoundary'

// Route-level code splitting (Phase 70.1.b): every page loads on
// demand so heavy dependencies (mapbox-gl, charts) leave the main
// bundle. Pages use named exports; lazy() needs a default.
import { redirectForRetiredPath, RETIRED_GHOST_PATH } from './lib/retiredSurfaces'
const Home = lazy(() => import('./pages/dashboard/Home'))
// GHOST-IS-HOME-1 — Ghost is the home; the dashboard keeps every line and moves.
const GhostHome = lazy(() =>
  import('./pages/dashboard/GhostHome').then((m) => ({ default: m.GhostHome })),
)
// INVITE-A-FRIEND-1 — the screen behind the line in the subdivider.
const ReferralsPage = lazy(() =>
  import('./pages/dashboard/settings/ReferralsPage').then((m) => ({ default: m.ReferralsPage })),
)
// ONE-GHOST-2 — the approval queue's own home, mounted BEFORE the Ghost
// page is retired so nothing is ever unreachable in between.
const ApprovalsPage = lazy(() => import('./pages/dashboard/ApprovalsPage'))
const SavedViewPage = lazy(() => import('./pages/dashboard/SavedViewPage'))
// BUILT-THING-IS-A-TAB-1 — a generated thing at its own address, in the shell.
const BuiltThingPage = lazy(() => import('./pages/dashboard/BuiltThingPage'))
const ConnectorsPage = lazy(() => import('./pages/dashboard/ConnectorsPage'))
const ConnectorsPreview = lazy(() => import('./pages/dev/ConnectorsPreview'))
const FirstHelloPreview = lazy(() =>
  import('./components/activation/FirstHelloPreview').then((m) => ({
    default: m.FirstHelloPreview,
  })),
)
// CAP-SPIKE-5: the native shell must not open on the sales page.
import { isNativeShell } from './lib/nativeShell'
import { useAuth } from './hooks/useAuth'

const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })),
)
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })),
)

// FRONT-DOOR-1 — "/" is now the door you can use before you sign up. The
// scrolling explanation is not deleted; it moved to /learn, one quiet link
// away, for someone who wants to be told rather than shown.
const FrontDoor = lazy(() =>
  import('./components/frontdoor/FrontDoor').then((m) => ({ default: m.FrontDoor })),
)
const Login = lazy(() => import('./pages/auth/Login').then((m) => ({ default: m.Login })))
const Signup = lazy(() => import('./pages/auth/Signup').then((m) => ({ default: m.Signup })))
const ForgotPassword = lazy(() =>
  import('./pages/auth/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
)
const ResetPassword = lazy(() =>
  import('./pages/auth/ResetPassword').then((m) => ({ default: m.ResetPassword })),
)
// THE-LINK-CANNOT-SIGN-YOU-IN-1 — every emailed link lands here, and this is
// the only page that turns anything in an address into a session.
const ConfirmLink = lazy(() =>
  import('./pages/auth/ConfirmLink').then((m) => ({ default: m.ConfirmLink })),
)
const InviteAccept = lazy(() =>
  import('./pages/auth/InviteAccept').then((m) => ({ default: m.InviteAccept })),
)
const OnboardingShell = lazy(() =>
  import('./pages/onboarding/OnboardingShell').then((m) => ({ default: m.OnboardingShell })),
)
const IndustryStep = lazy(() =>
  import('./pages/onboarding/IndustryStep').then((m) => ({ default: m.IndustryStep })),
)
const TeamStep = lazy(() =>
  import('./pages/onboarding/TeamStep').then((m) => ({ default: m.TeamStep })),
)
const FeaturesStep = lazy(() =>
  import('./pages/onboarding/FeaturesStep').then((m) => ({ default: m.FeaturesStep })),
)
const ImportStep = lazy(() =>
  import('./pages/onboarding/ImportStep').then((m) => ({ default: m.ImportStep })),
)
const ScheduleStep = lazy(() =>
  import('./pages/onboarding/ScheduleStep').then((m) => ({ default: m.ScheduleStep })),
)
const TourStep = lazy(() =>
  import('./pages/onboarding/TourStep').then((m) => ({ default: m.TourStep })),
)
const SpecialistShell = lazy(() =>
  import('./pages/onboarding-queue/SpecialistShell').then((m) => ({ default: m.SpecialistShell })),
)
const OnboardingQueue = lazy(() =>
  import('./pages/onboarding-queue/OnboardingQueue').then((m) => ({ default: m.OnboardingQueue })),
)
const AssignmentDetail = lazy(() =>
  import('./pages/onboarding-queue/AssignmentDetail').then((m) => ({
    default: m.AssignmentDetail,
  })),
)
const SpecialistProfile = lazy(() =>
  import('./pages/onboarding-queue/SpecialistProfile').then((m) => ({
    default: m.SpecialistProfile,
  })),
)
const AdminOnboardingSpecialists = lazy(() =>
  import('./pages/dashboard/admin/OnboardingSpecialists').then((m) => ({
    default: m.OnboardingSpecialists,
  })),
)
// ADMIN-1: find any account and see everything about it.
const AdminHealth = lazy(() =>
  import('./pages/dashboard/admin/AdminHealth').then((m) => ({ default: m.AdminHealth })),
)
const AdminAccounts = lazy(() =>
  import('./pages/dashboard/admin/AdminAccounts').then((m) => ({ default: m.AdminAccounts })),
)
const AdminAffiliates = lazy(() =>
  import('./pages/dashboard/admin/AdminAffiliates').then((m) => ({ default: m.AdminAffiliates })),
)
const AdminNurtureSequences = lazy(() =>
  import('./pages/dashboard/admin/NurtureSequences').then((m) => ({
    default: m.AdminNurtureSequences,
  })),
)
const AdminAffiliateEvents = lazy(() =>
  import('./pages/dashboard/admin/AdminAffiliateEvents').then((m) => ({
    default: m.AdminAffiliateEvents,
  })),
)
const AdminAffiliateDetail = lazy(() =>
  import('./pages/dashboard/admin/AdminAffiliateDetail').then((m) => ({
    default: m.AdminAffiliateDetail,
  })),
)
const AdminAffiliateClawbacks = lazy(() =>
  import('./pages/dashboard/admin/AdminAffiliateClawbacks').then((m) => ({
    default: m.AdminAffiliateClawbacks,
  })),
)
const AdminBonuses = lazy(() =>
  import('./pages/dashboard/admin/Bonuses').then((m) => ({ default: m.AdminBonuses })),
)
const AiPricing = lazy(() =>
  import('./pages/dashboard/admin/AiPricing').then((m) => ({ default: m.AiPricing })),
)
const AdminCostDictionary = lazy(() =>
  import('./pages/dashboard/admin/CostDictionary').then((m) => ({ default: m.CostDictionary })),
)
const OnboardingAnalytics = lazy(() =>
  import('./pages/dashboard/admin/OnboardingAnalytics').then((m) => ({
    default: m.OnboardingAnalytics,
  })),
)
const ActivationFunnel = lazy(() =>
  import('./pages/dashboard/admin/ActivationFunnel').then((m) => ({ default: m.ActivationFunnel })),
)
const AdminWorkflows = lazy(() =>
  import('./pages/dashboard/admin/Workflows').then((m) => ({ default: m.Workflows })),
)
const AdminAudiences = lazy(() =>
  import('./pages/dashboard/admin/Audiences').then((m) => ({ default: m.Audiences })),
)
const Campaigns = lazy(() =>
  import('./pages/dashboard/marketing/Campaigns').then((m) => ({ default: m.Campaigns })),
)
// MARKETING-RETURN-1: the Return page, the front door of Marketing.
// THE-ROUTE-EXISTS-1 — the two section landing addresses that were declared
// nowhere. `/dashboard/marketing` is the root of a namespace four of its own
// children live in, so it resolves to a page built from the menu;
// `/dashboard/team` is a label over six pages that live elsewhere, so it
// resolves to the first child the role can see.
const MarketingSection = lazy(() =>
  import('./pages/dashboard/section/SectionAddress').then((m) => ({ default: m.MarketingSection })),
)
const TeamSection = lazy(() =>
  import('./pages/dashboard/section/SectionAddress').then((m) => ({ default: m.TeamSection })),
)
const MarketingReturn = lazy(() =>
  import('./pages/dashboard/marketing/Return').then((m) => ({ default: m.Return })),
)
// DASH-PRESETS-1: the Channels index and the per platform dashboard page are
// retired. Both are now tabs on the Home switcher, and the routes below
// redirect rather than 404 so nobody's bookmark breaks.
const AdminDemoAccounts = lazy(() =>
  import('./pages/dashboard/admin/DemoAccounts').then((m) => ({ default: m.DemoAccounts })),
)
const Editor = lazy(() => import('./pages/dashboard/Editor').then((m) => ({ default: m.Editor })))
const Analytics = lazy(() =>
  import('./pages/dashboard/Analytics').then((m) => ({ default: m.Analytics })),
)
// ANALYTICS-UX-1: standalone per-site analytics page.
const SiteAnalytics = lazy(() =>
  import('./pages/dashboard/analytics/SiteAnalytics').then((m) => ({ default: m.SiteAnalytics })),
)
const Leads = lazy(() => import('./pages/dashboard/Leads').then((m) => ({ default: m.Leads })))
const LeadMapPage = lazy(() =>
  import('./pages/dashboard/LeadMapPage').then((m) => ({ default: m.LeadMapPage })),
)
const Leaderboard = lazy(() =>
  import('./pages/dashboard/Leaderboard').then((m) => ({ default: m.Leaderboard })),
)
const LeaderboardTV = lazy(() =>
  import('./pages/dashboard/LeaderboardTV').then((m) => ({ default: m.LeaderboardTV })),
)
const Inbox = lazy(() => import('./pages/dashboard/Inbox').then((m) => ({ default: m.Inbox })))
const InboxWidgetSettings = lazy(() =>
  import('./pages/dashboard/inbox/WidgetSettings').then((m) => ({ default: m.WidgetSettings })),
)
const InboxEmailDomain = lazy(() =>
  import('./pages/dashboard/inbox/EmailDomain').then((m) => ({ default: m.EmailDomain })),
)
const InboxPhoneSetup = lazy(() =>
  import('./pages/dashboard/inbox/PhoneSetup').then((m) => ({ default: m.PhoneSetup })),
)
const InboxWhatsApp = lazy(() =>
  import('./pages/dashboard/inbox/WhatsApp').then((m) => ({ default: m.WhatsApp })),
)
const AdminWhatsAppSettings = lazy(() =>
  import('./pages/dashboard/admin/WhatsAppSettings').then((m) => ({ default: m.WhatsAppSettings })),
)
const InboxMessenger = lazy(() =>
  import('./pages/dashboard/inbox/Messenger').then((m) => ({ default: m.Messenger })),
)
const AdminMetaConnections = lazy(() =>
  import('./pages/dashboard/admin/MetaConnections').then((m) => ({ default: m.MetaConnections })),
)
// CONNECT-2 W2: the Connections hub replaces the separate MetaLeadgen
// and GoogleLeadAds settings pages (their routes redirect here).
const ConnectionsHub = lazy(() =>
  import('./pages/dashboard/settings/integrations/ConnectionsHub').then((m) => ({
    default: m.ConnectionsHub,
  })),
)

/** Redirect that keeps the query string (the Meta OAuth callback lands
 *  on the old page path with ?connected=/?error= params; MONEY-IA-2 uses it
 *  for the subscribe/top-up return urls too). */
function RedirectPreservingSearch({ to }: { to: string }) {
  return <Navigate to={withCurrentSearch(to)} replace />
}
const InboxInstagram = lazy(() =>
  import('./pages/dashboard/inbox/Instagram').then((m) => ({ default: m.Instagram })),
)
const SettingsBankAccount = lazy(() =>
  import('./pages/dashboard/settings/BankAccount').then((m) => ({ default: m.BankAccount })),
)
const ReviewLanding = lazy(() =>
  import('./pages/public/ReviewLanding').then((m) => ({ default: m.ReviewLanding })),
)
const AdminReputationSettings = lazy(() =>
  import('./pages/dashboard/admin/ReputationSettings').then((m) => ({
    default: m.ReputationSettings,
  })),
)
const AdminInternalReviews = lazy(() =>
  import('./pages/dashboard/admin/InternalReviews').then((m) => ({ default: m.InternalReviews })),
)
const Widget = lazy(() => import('./pages/Widget').then((m) => ({ default: m.Widget })))
const FeesPage = lazy(() =>
  import('./pages/dashboard/billing/Fees').then((m) => ({ default: m.Fees })),
)
// MONEY-IA-2: Plan and Usage merged into one page under Money > What you pay.
const PlanAndUsage = lazy(() =>
  import('./pages/dashboard/money/PlanAndUsage').then((m) => ({ default: m.PlanAndUsage })),
)
// SALES-UI-1: the Sales analytics area (Overview + Reps built; rest coming soon).
const SalesAnalytics = lazy(() =>
  import('./pages/dashboard/sales/SalesAnalytics').then((m) => ({ default: m.SalesAnalytics })),
)
const Settings = lazy(() =>
  import('./pages/dashboard/Settings').then((m) => ({ default: m.Settings })),
)
const Payments = lazy(() =>
  import('./pages/dashboard/Payments').then((m) => ({ default: m.Payments })),
)
const Team = lazy(() => import('./pages/dashboard/Team').then((m) => ({ default: m.Team })))
const TeamMember = lazy(() =>
  import('./pages/dashboard/settings/TeamMember').then((m) => ({ default: m.TeamMember })),
)
const AccountCalendar = lazy(() =>
  import('./pages/dashboard/account/Calendar').then((m) => ({ default: m.Calendar })),
)
const CalendarForMember = lazy(() =>
  import('./pages/dashboard/team/CalendarForMember').then((m) => ({
    default: m.CalendarForMember,
  })),
)
const SettingsCommissions = lazy(() =>
  import('./pages/dashboard/settings/Commissions').then((m) => ({ default: m.Commissions })),
)
const MyCommissions = lazy(() =>
  import('./pages/dashboard/MyCommissions').then((m) => ({ default: m.MyCommissions })),
)
const Jobs = lazy(() => import('./pages/dashboard/Jobs').then((m) => ({ default: m.Jobs })))
// Wave N3c: the real job page (object with money/lifecycle -> page).
const JobDetail = lazy(() =>
  import('./pages/dashboard/JobDetail').then((m) => ({ default: m.JobDetail })),
)
const Clock = lazy(() => import('./pages/dashboard/Clock').then((m) => ({ default: m.Clock })))
const Payroll = lazy(() =>
  import('./pages/dashboard/Payroll').then((m) => ({ default: m.Payroll })),
)
const MyPaychecks = lazy(() =>
  import('./pages/dashboard/MyPaychecks').then((m) => ({ default: m.MyPaychecks })),
)
const PayPeriodSettings = lazy(() =>
  import('./pages/dashboard/settings/PayPeriodSettings').then((m) => ({
    default: m.PayPeriodSettings,
  })),
)
const SettingsBonuses = lazy(() =>
  import('./pages/dashboard/settings/Bonuses').then((m) => ({ default: m.Bonuses })),
)
const NotificationSettings = lazy(() =>
  import('./pages/dashboard/settings/Notifications').then((m) => ({
    default: m.NotificationSettings,
  })),
)
// Wave N4b: public quote settings (personal message, trust strip,
// what happens next).
const SettingsQuotePage = lazy(() =>
  import('./pages/dashboard/settings/QuotePage').then((m) => ({ default: m.QuotePage })),
)
// Wave P4: customer portal blocks (reorder + toggle portal sections).
const SettingsPortalPage = lazy(() =>
  import('./pages/dashboard/settings/PortalPage').then((m) => ({ default: m.PortalPage })),
)
// Wave F2: lead source vocabulary (built in list + merchant-owned sources).
const SettingsLeadSourcesPage = lazy(() =>
  import('./pages/dashboard/settings/LeadSourcesPage').then((m) => ({
    default: m.LeadSourcesPage,
  })),
)
// Wave F3: canvass disposition dictionary (built in flags + merchant-owned rows).
const SettingsCanvassDispositionsPage = lazy(() =>
  import('./pages/dashboard/settings/CanvassDispositionsPage').then((m) => ({
    default: m.CanvassDispositionsPage,
  })),
)
// Wave F4: merchant vocabulary (the four nouns + job status labels).
const SettingsVocabularyPage = lazy(() =>
  import('./pages/dashboard/settings/VocabularyPage').then((m) => ({
    default: m.VocabularyPage,
  })),
)
// Wave F5: merchant message templates (the automatic emails + texts).
const SettingsMessagesPage = lazy(() =>
  import('./pages/dashboard/settings/MessagesPage').then((m) => ({
    default: m.MessagesPage,
  })),
)
const MemoryPage = lazy(() =>
  import('./pages/dashboard/settings/MemoryPage').then((m) => ({ default: m.MemoryPage })),
)
const ServiceBotSettings = lazy(() =>
  import('./pages/dashboard/settings/ServiceBotSettings').then((m) => ({
    default: m.ServiceBotSettings,
  })),
)
const CrmImport = lazy(() =>
  import('./pages/dashboard/settings/CrmImport').then((m) => ({ default: m.CrmImport })),
)
const SettingsBranding = lazy(() =>
  import('./pages/dashboard/settings/Branding').then((m) => ({ default: m.Branding })),
)
// THEME-4b: themes settings page + live document theme editor.
const SettingsThemes = lazy(() =>
  import('./pages/dashboard/settings/ThemesPage').then((m) => ({ default: m.ThemesPage })),
)
// PLATFORM-1: the menu editor.
const SettingsNavigation = lazy(() =>
  import('./pages/dashboard/settings/NavigationPage').then((m) => ({
    default: m.NavigationPage,
  })),
)
// RECORD-TYPES-1: the generic screens for EVERY custom record type. Two
// routes, no page per type.
// ALIVE-1 — where a build is watched as it happens.
const BuildingPage = lazy(() =>
  import('./pages/dashboard/BuildingPage').then((m) => ({ default: m.BuildingPage })),
)
// THE-PLAN-IS-SHOWN-1 — the plan, read and changed before anything is built.
const PlanReviewPage = lazy(() =>
  import('./pages/dashboard/PlanReviewPage').then((m) => ({ default: m.PlanReviewPage })),
)
// THE-FIRST-BUILD-SCREEN-1 — where a verified person lands, once.
const FirstBuildPage = lazy(() =>
  import('./pages/FirstBuildPage').then((m) => ({ default: m.FirstBuildPage })),
)
// THE-BUILT-THING-ARRIVES-1 — where a waiting plan is found.
const BuildsPage = lazy(() =>
  import('./pages/dashboard/BuildsPage').then((m) => ({ default: m.BuildsPage })),
)
const CustomRecordList = lazy(() =>
  import('./pages/dashboard/records/CustomRecordListPage').then((m) => ({
    default: m.CustomRecordListPage,
  })),
)
const CustomRecordDetail = lazy(() =>
  import('./pages/dashboard/records/CustomRecordDetailPage').then((m) => ({
    default: m.CustomRecordDetailPage,
  })),
)
// TEMPLATES-1: browse what the product can do and switch a part on.
const SettingsModuleLibrary = lazy(() =>
  import('./pages/dashboard/settings/ModuleLibraryPage').then((m) => ({
    default: m.ModuleLibraryPage,
  })),
)
// RECORD-TYPES-1: the type editor.
const SettingsRecordTypes = lazy(() =>
  import('./pages/dashboard/settings/RecordTypesPage').then((m) => ({
    default: m.RecordTypesPage,
  })),
)
// CUSTOM-FIELDS-1: the field editor.
const SettingsCustomFields = lazy(() =>
  import('./pages/dashboard/settings/CustomFieldsPage').then((m) => ({
    default: m.CustomFieldsPage,
  })),
)
// SANDBOX-1: the one visible proof that the artifact container works.
const SettingsArtifacts = lazy(() =>
  import('./pages/dashboard/settings/ArtifactsPage').then((m) => ({ default: m.ArtifactsPage })),
)
// Activation Hub: the permanent Settings > Setup surface.
const SettingsSetup = lazy(() => import('./pages/dashboard/settings/Setup'))
const SettingsBusinessHours = lazy(() =>
  import('./pages/dashboard/settings/BusinessHours').then((m) => ({ default: m.BusinessHours })),
)
const SettingsServiceArea = lazy(() =>
  import('./pages/dashboard/settings/ServiceArea').then((m) => ({ default: m.ServiceArea })),
)
const SettingsAgreements = lazy(() =>
  import('./pages/dashboard/settings/Agreements').then((m) => ({ default: m.Agreements })),
)
const Payouts = lazy(() =>
  import('./pages/dashboard/Payouts').then((m) => ({ default: m.Payouts })),
)
const Reactivation = lazy(() =>
  import('./pages/dashboard/Reactivation').then((m) => ({ default: m.Reactivation })),
)
const Tasks = lazy(() => import('./pages/dashboard/Tasks').then((m) => ({ default: m.Tasks })))
const RunHistory = lazy(() =>
  import('./pages/dashboard/automations/RunHistory').then((m) => ({ default: m.RunHistory })),
)
const AutomationsHub = lazy(() =>
  import('./pages/dashboard/automations/Index').then((m) => ({ default: m.AutomationsHub })),
)
const WorkflowDetail = lazy(() =>
  import('./pages/dashboard/automations/WorkflowDetail').then((m) => ({
    default: m.WorkflowDetail,
  })),
)
// AGENTS-HOME-1 — one scheduled agent's own page: what it does, when it runs
// next, what it produced last time, what a run costs, and its switch.
const AgentDetail = lazy(() =>
  import('./pages/dashboard/automations/AgentDetail').then((m) => ({
    default: m.AgentDetail,
  })),
)
const Billing = lazy(() =>
  import('./pages/dashboard/Billing').then((m) => ({ default: m.Billing })),
)
const AffiliateDashboard = lazy(() =>
  import('./pages/AffiliateDashboard').then((m) => ({ default: m.AffiliateDashboard })),
)
const AffiliateStatements = lazy(() =>
  import('./pages/dashboard/affiliate/Statements').then((m) => ({ default: m.Statements })),
)
const Dispatch = lazy(() =>
  import('./pages/dashboard/operations/Dispatch').then((m) => ({ default: m.Dispatch })),
)
const TechView = lazy(() =>
  import('./pages/dashboard/operations/TechView').then((m) => ({ default: m.TechView })),
)
const Pages = lazy(() => import('./pages/dashboard/Pages').then((m) => ({ default: m.Pages })))
const Plans = lazy(() => import('./pages/dashboard/Plans').then((m) => ({ default: m.Plans })))
// Wave N4a: the services price book manager.
const Services = lazy(() =>
  import('./pages/dashboard/Services').then((m) => ({ default: m.Services })),
)
const Portfolio = lazy(() =>
  import('./pages/dashboard/Portfolio').then((m) => ({ default: m.Portfolio })),
)
const Customers = lazy(() =>
  import('./pages/dashboard/Customers').then((m) => ({ default: m.Customers })),
)
const Pipelines = lazy(() =>
  import('./pages/dashboard/Pipelines').then((m) => ({ default: m.Pipelines })),
)
const PipelineTemplateGallery = lazy(() =>
  import('./pages/dashboard/pipelines/PipelineTemplateGallery').then((m) => ({
    default: m.PipelineTemplateGallery,
  })),
)
const CustomerDetail = lazy(() =>
  import('./pages/dashboard/CustomerDetail').then((m) => ({ default: m.CustomerDetail })),
)
const CustomerImport = lazy(() =>
  import('./pages/dashboard/customers/CustomerImport').then((m) => ({ default: m.CustomerImport })),
)
const Quotes = lazy(() => import('./pages/dashboard/Quotes').then((m) => ({ default: m.Quotes })))
const Invoices = lazy(() =>
  import('./pages/dashboard/Invoices').then((m) => ({ default: m.Invoices })),
)
const QuoteDetail = lazy(() =>
  import('./pages/dashboard/QuoteDetail').then((m) => ({ default: m.QuoteDetail })),
)
const AiStudio = lazy(() =>
  import('./pages/dashboard/ai/AiStudio').then((m) => ({ default: m.AiStudio })),
)
const TeamPhotos = lazy(() =>
  import('./pages/dashboard/ai/TeamPhotos').then((m) => ({ default: m.TeamPhotos })),
)
const CoverPhoto = lazy(() =>
  import('./pages/dashboard/ai/CoverPhoto').then((m) => ({ default: m.CoverPhoto })),
)
const Logo = lazy(() => import('./pages/dashboard/ai/Logo').then((m) => ({ default: m.Logo })))
const Writer = lazy(() =>
  import('./pages/dashboard/ai/Writer').then((m) => ({ default: m.Writer })),
)
const History = lazy(() =>
  import('./pages/dashboard/ai/History').then((m) => ({ default: m.History })),
)
const PublicQuote = lazy(() =>
  import('./pages/public/PublicQuote').then((m) => ({ default: m.PublicQuote })),
)
const PublicInvoice = lazy(() =>
  import('./pages/public/PublicInvoice').then((m) => ({ default: m.PublicInvoice })),
)
const PublicServicePage = lazy(() =>
  import('./pages/public/PublicServicePage').then((m) => ({ default: m.PublicServicePage })),
)
const CustomerPortal = lazy(() =>
  import('./pages/portal/CustomerPortal').then((m) => ({ default: m.CustomerPortal })),
)
const SitePreview = lazy(() =>
  import('./pages/preview/SitePreview').then((m) => ({ default: m.SitePreview })),
)
const UnifiedPublicResolver = lazy(() =>
  import('./pages/public/UnifiedPublicResolver').then((m) => ({
    default: m.UnifiedPublicResolver,
  })),
)
const SitesHome = lazy(() =>
  import('./pages/dashboard/sites/SitesHome').then((m) => ({ default: m.SitesHome })),
)
const SiteEditor = lazy(() =>
  import('./pages/dashboard/sites/Editor').then((m) => ({ default: m.SiteEditor })),
)
const RescheduleAppointment = lazy(() =>
  import('./pages/public/RescheduleAppointment').then((m) => ({
    default: m.RescheduleAppointment,
  })),
)
const CancelAppointment = lazy(() =>
  import('./pages/public/CancelAppointment').then((m) => ({ default: m.CancelAppointment })),
)
const Terms = lazy(() => import('./pages/legal/Terms').then((m) => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/legal/Privacy').then((m) => ({ default: m.Privacy })))
const AcceptableUse = lazy(() =>
  import('./pages/legal/AcceptableUse').then((m) => ({ default: m.AcceptableUse })),
)
const AgencyClients = lazy(() =>
  import('./pages/dashboard/agency/Clients').then((m) => ({ default: m.AgencyClients })),
)
const AgencyClientDetail = lazy(() =>
  import('./pages/dashboard/agency/ClientDetail').then((m) => ({ default: m.AgencyClientDetail })),
)
const AgencyReporting = lazy(() =>
  import('./pages/dashboard/agency/AgencyReporting').then((m) => ({ default: m.AgencyReporting })),
)
const AffiliateAgreement = lazy(() =>
  import('./pages/legal/AffiliateAgreement').then((m) => ({ default: m.AffiliateAgreement })),
)
const Affiliates = lazy(() => import('./pages/Affiliates').then((m) => ({ default: m.Affiliates })))
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })))

/**
 * THE-DOOR-TO-THE-BUILD-1 — the gate in front of the public site resolver.
 *
 * Not lazy and not a page: it decides between two things already imported
 * here, so a 404 does not wait on a chunk it will never render.
 */
function PublicAddress({ children }: { children: ReactNode }) {
  const { slugOrUsername } = useParams<{ slugOrUsername: string }>()
  return isPublicAddress(slugOrUsername) ? <>{children}</> : <NotFound />
}

/**
 * DASH-PRESETS-1 — an old /dashboard/marketing/channels/:platform bookmark.
 *
 * The dashboard it pointed at still exists; it is a TAB now. So the URL is
 * translated rather than dropped: the platform slug is the dashboard key, and
 * Home opens straight onto it. A 404 here would have cost a merchant the one
 * screen they had bookmarked.
 */
/**
 * WORDING-3 — the old assistant path, kept forever.
 *
 * IT MUST CARRY THE QUERY STRING. panelDeepLink builds
 * `?chat=<id>` and `?bot=<kind>:<ref>`, and React Router's <Navigate to="/x">
 * DROPS location.search. A plain Navigate here would resolve, render, and
 * silently open the wrong thing: every old panel-expand bookmark would land on
 * the assistant with no bot and no thread selected, which looks like the page
 * working rather than like a redirect that lost its argument. That is the
 * failure this component exists to prevent, and the guard asserts it with a
 * query string specifically.
 */
/**
 * NATIVE-SCHEME-1: closes the browser sheet when a flow returns to
 * ghostcrm://, then routes the app to where the flow said to go.
 *
 * Lives inside BrowserRouter so it can navigate, and is a no-op on the web.
 */
function NativeReturnHandler() {
  const navigate = useNavigate()
  useEffect(() => {
    let dispose: (() => void) | undefined
    void registerNativeReturnHandler((to) => navigate(to, { replace: true })).then((d) => {
      dispose = d
    })
    return () => dispose?.()
  }, [navigate])
  return null
}

/**
 * ONE-GHOST-2 — THE GHOST PAGE IS RETIRED, AND NOTHING 404s.
 *
 * Both the old /dashboard/servicebot path and /dashboard/ghost itself land
 * here. A link that named a PROPOSAL is carried through to the approval
 * queue's new home, because that is the one deep link that pointed at real
 * work. Everything else pointed at a conversation or a "Bot", and both of
 * those are the panel now, which opens over the dashboard.
 */
function RetiredGhostPageRedirect() {
  const { search } = useLocation()
  return <Navigate to={redirectForRetiredPath(`${RETIRED_GHOST_PATH}${search}`)} replace />
}

function RetiredChannelRedirect() {
  const { platform } = useParams<{ platform: string }>()
  return <Navigate to={`/dashboard?dashboard=${encodeURIComponent(platform ?? 'home')}`} replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <NativeReturnHandler />
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <ThemeProvider>
            <AuthProvider>
              <AiJobsProvider>
                <VoiceDialerProvider>
                  <Suspense fallback={<RouteLoading />}>
                    <Routes>
                      <Route path="/" element={<RootRoute />} />
                      <Route
                        path="/signup"
                        element={
                          <WebOnlyRoute>
                            <Signup />
                          </WebOnlyRoute>
                        }
                      />
                      {/* Pre-launch 2026-06-10: the two-step claim flow is gone;
                    /signup/account bookmarks land on the merged form. */}
                      <Route path="/signup/account" element={<Navigate to="/signup" replace />} />
                      {/* FRONT-DOOR-1: the deeper page. Nothing was deleted;
                    the hero, the gradient and the five businesses live here. */}
                      <Route
                        path="/learn"
                        element={
                          <WebOnlyRoute>
                            <LandingPage />
                          </WebOnlyRoute>
                        }
                      />
                      {/* THE-PRICING-PAGE-1: the ruled ladder, public. Web only,
                          like every page that sells: no price inside the app. */}
                      <Route
                        path="/pricing"
                        element={
                          <WebOnlyRoute>
                            <PricingPage />
                          </WebOnlyRoute>
                        }
                      />
                      <Route path="/login" element={<Login />} />
                      {/* THE-FIRST-BUILD-SCREEN-1 — "after signup with email
                          verified, never before". No capability of its own: it
                          reads the sentence they typed and asks the planner,
                          and the plan it opens carries its own gate. */}
                      <Route
                        path="/first-build"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <VerifiedOnly>
                              <FirstBuildPage />
                            </VerifiedOnly>
                          </AuthGuard>
                        }
                      />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/auth/confirm" element={<ConfirmLink />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/acceptable-use" element={<AcceptableUse />} />
                      {/* Wave 2A: standalone Affiliate Agreement (separate from
                main ToS per PRD line 7966). */}
                      <Route path="/affiliate-agreement" element={<AffiliateAgreement />} />
                      {/* Wave 2B: public affiliate program landing. Smart CTA
                redirects authed visitors to /dashboard/affiliate
                (enrollment hero) and unauthed visitors to /signup. */}
                      <Route
                        path="/affiliates"
                        element={
                          <WebOnlyRoute>
                            <Affiliates />
                          </WebOnlyRoute>
                        }
                      />
                      <Route path="/invite/:token" element={<InviteAccept />} />

                      {/* Phase O2: niche-aware setup wizard. The
                OnboardingShell layout renders header chrome
                and an Outlet for the active step. The
                OnboardingRouteGuard inside the shell handles
                auth, role gating (owner+admin only), and
                bare-/onboarding redirects to the next
                incomplete step. */}
                      <Route element={<OnboardingShell />}>
                        {/* Bare /onboarding renders nothing while progress
                loads; the guard's resume effect then navigates to
                the next incomplete step. (A static Navigate to
                /onboarding/welcome here used to beat the guard and
                restart every returning user at welcome.) */}
                        <Route path="/onboarding" element={null} />
                        <Route path="/onboarding/industry" element={<IndustryStep />} />
                        <Route path="/onboarding/team" element={<TeamStep />} />
                        <Route path="/onboarding/features" element={<FeaturesStep />} />
                        <Route path="/onboarding/import" element={<ImportStep />} />
                        <Route path="/onboarding/schedule" element={<ScheduleStep />} />
                        <Route path="/onboarding/tour" element={<TourStep />} />
                      </Route>

                      {/* Phase O4: VA queue. Mounted as a layout route
                with its own SpecialistShell (no AppShell). The
                shell handles auth + role gating. The
                completed and profile children must be defined
                BEFORE the catch-all :assignment_id child so
                literal segments win. */}
                      <Route element={<SpecialistShell />}>
                        <Route path="/onboarding-queue" element={<OnboardingQueue />} />
                        <Route path="/onboarding-queue/completed" element={<OnboardingQueue />} />
                        <Route path="/onboarding-queue/profile" element={<SpecialistProfile />} />
                        <Route
                          path="/onboarding-queue/:assignment_id"
                          element={<AssignmentDetail />}
                        />
                      </Route>

                      {/* Phase A2: /dashboard redirects to the first
                sub-page of the first section the user has
                access to. Owner+admin land on Sales -> Pipelines;
                an agent without Sales gating still lands there
                today, but the role-aware filter keeps this
                future-proof for restricted accounts. */}
                      <Route
                        path="/dashboard"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <GhostHome />
                          </AuthGuard>
                        }
                      />
                      {/* GHOST-IS-HOME-1 — the dashboard that was the home. Not
                          deleted and not changed: it is no longer the FIRST
                          thing, because the first thing is where they build. */}
                      <Route
                        path="/dashboard/settings/referrals"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <ReferralsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/overview"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <Home />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 4 Lane 3: multi-business portfolio view. */}
                      <Route
                        path="/dashboard/portfolio"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <Portfolio />
                          </AuthGuard>
                        }
                      />

                      {/* Phase A: placeholder routes for nav slots whose
                real features have not shipped yet. Each renders
                PlaceholderPage with a "Coming soon" treatment. */}
                      <Route
                        path="/dashboard/opportunities"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <PlaceholderPage
                              title="Opportunities"
                              sectionLabel="Pipelines"
                              sectionPath="/dashboard/pipelines"
                              description="A flat list of every deal across all pipelines is on the way. For now, open a pipeline to see your opportunities on the kanban."
                            />
                          </AuthGuard>
                        }
                      />
                      {/* Jobs and Calendar unified into one surface. The legacy
                        calendar route redirects to the Week view so existing
                        bookmarks keep working. */}
                      <Route
                        path="/dashboard/calendar"
                        element={<Navigate to="/dashboard/jobs?view=week" replace />}
                      />
                      {/* Wave N1 IA reorg: semantic landing paths for the new
                        nav sections resolve to each section's first child.
                        /dashboard/operations never had an index route (only
                        /dispatch and /tech-view), so this adds a home for the
                        retired Operations section slug too. */}
                      <Route
                        path="/dashboard/schedule"
                        element={<Navigate to="/dashboard/jobs" replace />}
                      />
                      <Route
                        path="/dashboard/sales"
                        element={<Navigate to="/dashboard/quotes" replace />}
                      />
                      <Route
                        path="/dashboard/operations"
                        element={<Navigate to="/dashboard/jobs" replace />}
                      />
                      {/* Wave 10 — tasks list page (workflow create_task action
                    now writes here; manual create/edit also). */}
                      <Route
                        path="/dashboard/tasks"
                        element={
                          <AuthGuard requireCapability="tasks.manage">
                            <Tasks />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 17 — Automations hub (Templates/Ingestion/Workflows tabs). */}
                      <Route
                        path="/dashboard/automations"
                        element={
                          <AuthGuard requireCapability="automations.view">
                            <AutomationsHub />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 17 — single workflow detail (read-only graph view). */}
                      <Route
                        path="/dashboard/automations/workflows/:id"
                        element={
                          <AuthGuard requireCapability="automations.manage">
                            <WorkflowDetail />
                          </AuthGuard>
                        }
                      />
                      {/* AGENTS-HOME-1 — a scheduled agent's own page. Same
                          capability as a workflow's, because the switch on it
                          starts work that spends the merchant's money. */}
                      <Route
                        path="/dashboard/automations/agents/:id"
                        element={
                          <AuthGuard requireCapability="automations.manage">
                            <AgentDetail />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 16 — workflow run history (owner/admin gated in-page). */}
                      <Route
                        path="/dashboard/automations/runs"
                        element={
                          <AuthGuard requireCapability="automations.runs_view">
                            <RunHistory />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 13 — real Payouts page replaces the PlaceholderPage. */}
                      <Route
                        path="/dashboard/payouts"
                        element={
                          <AuthGuard requireCapability="money.section.view">
                            <Payouts />
                          </AuthGuard>
                        }
                      />
                      {/* Activation Hub: reopenable full setup surface. */}
                      <Route
                        path="/dashboard/settings/setup"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsSetup />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 13 — real Branding page replaces the PlaceholderPage. */}
                      <Route
                        path="/dashboard/settings/branding"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsBranding />
                          </AuthGuard>
                        }
                      />
                      {/* PLATFORM-1 — the menu editor. */}
                      <Route
                        path="/dashboard/settings/navigation"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsNavigation />
                          </AuthGuard>
                        }
                      />
                      {/* RECORD-TYPES-1 — one list and one detail route serve
                          EVERY record type a business invents. The pages
                          resolve the type from the slug; nothing is written
                          per type. Gated on records.manage, the capability
                          this wave added, so the route and the RLS policy
                          agree. */}
                      {/* ALIVE-1 — the build has a place. Approving takes the
                          merchant here and they watch it appear. Gated on
                          records.manage: a build changes the account's own
                          structure, which is what that capability means, and
                          the catalog has no narrower key for "may build". The
                          role gate guard refuses any signed-in route with no
                          capability at all, and it was right to. */}
                      <Route
                        path="/dashboard/builds"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <BuildsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/building/:planId"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <VerifiedOnly>
                              <BuildingPage />
                            </VerifiedOnly>
                          </AuthGuard>
                        }
                      />
                      {/* THE-PLAN-IS-SHOWN-1 — approving it is what builds it,
                          so it is gated exactly as the build it starts is. */}
                      <Route
                        path="/dashboard/plan/:planId"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <PlanReviewPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/records/:slug"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <CustomRecordList />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/records/:slug/:id"
                        element={
                          <AuthGuard requireCapability="records.manage">
                            <CustomRecordDetail />
                          </AuthGuard>
                        }
                      />
                      {/* MARKETPLACE-BROWSE-1 — the Feature Marketplace at its
                          own address, which is where the menu now points. Same
                          page, same gate. The settings address below still
                          resolves, because a bookmark is a promise. */}
                      <Route
                        path="/dashboard/marketplace"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsModuleLibrary />
                          </AuthGuard>
                        }
                      />
                      {/* TEMPLATES-1 — the add-ons library. Same gate as the
                          menu editor, because installing a module writes the
                          same account_navigation rows. */}
                      <Route
                        path="/dashboard/settings/modules"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsModuleLibrary />
                          </AuthGuard>
                        }
                      />
                      {/* RECORD-TYPES-1 — the type editor. */}
                      <Route
                        path="/dashboard/settings/record-types"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsRecordTypes />
                          </AuthGuard>
                        }
                      />
                      {/* CUSTOM-FIELDS-1 — a business adds its own fields to the
                          records that already exist. */}
                      <Route
                        path="/dashboard/settings/custom-fields"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsCustomFields />
                          </AuthGuard>
                        }
                      />
                      {/* SANDBOX-1 — merchant artifacts, listed and rendered in a
                          sandbox that reaches nothing. Nothing creates one yet. */}
                      <Route
                        path="/dashboard/settings/artifacts"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsArtifacts />
                          </AuthGuard>
                        }
                      />
                      {/* THEME-4b — themes list + live document theme editor. */}
                      <Route
                        path="/dashboard/settings/themes"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsThemes />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 13 — Business Hours editor. */}
                      <Route
                        path="/dashboard/settings/business-hours"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsBusinessHours />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 13 — Service Area picker. */}
                      <Route
                        path="/dashboard/settings/service-area"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsServiceArea />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 75.A — Agreement and document template library. */}
                      <Route
                        path="/dashboard/settings/agreements"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsAgreements />
                          </AuthGuard>
                        }
                      />
                      {/* THE-ACCOUNT-REMEMBERS-2 — what Ghost remembers about
                          this business, which is the Learned facts surface
                          moved out of the ServiceBot page and renamed. Same
                          capability as the page it left. */}
                      <Route
                        path="/dashboard/settings/memory"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <MemoryPage />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 62.J — Ghost toggle dashboard. */}
                      <Route
                        path="/dashboard/settings/service-bot"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <ServiceBotSettings />
                          </AuthGuard>
                        }
                      />
                      {/* Wave 12 — CRM Migration Assistant: import customers
                    from HCP / Jobber / GHL / generic CSV. */}
                      <Route
                        path="/dashboard/settings/import"
                        element={
                          <AuthGuard requireCapability="customers.import">
                            <CrmImport />
                          </AuthGuard>
                        }
                      />
                      {/* WORDING-3 — the assistant lives at /dashboard/ghost.
                          This is the ONE internal name that changes, because a
                          merchant reads it in the address bar. */}
                      <Route
                        path="/dashboard/approvals"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <ApprovalsPage />
                          </AuthGuard>
                        }
                      />
                      {/* BUILT-THING-IS-A-TAB-1 — a thing Ghost built, as a
                          tab like any other. Same rule as VIEWS-2 below: the
                          slug is the whole address and the business comes from
                          the caller, never from the URL. `team.view` matches
                          the saved view beside it, so a built thing is visible
                          to anyone who can see the business at all. */}
                      <Route
                        path="/dashboard/built/:slug"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <BuiltThingPage />
                          </AuthGuard>
                        }
                      />
                      {/* VIEWS-2 — a saved list Ghost built. The slug is the
                          whole address; the business is resolved from the
                          caller, never from the URL. */}
                      <Route
                        path="/dashboard/views/:slug"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <SavedViewPage />
                          </AuthGuard>
                        }
                      />
                      {/* CONNECT-2 dev preview: the grid without a session, so
                          its layout can actually be looked at. Dev builds only. */}
                      {import.meta.env.DEV && (
                        <Route path="/__connectors-preview" element={<ConnectorsPreview />} />
                      )}
                      {/* FIRST-HELLO-1 dev preview: the first-hello screen in all
                          four grant states, without a session. Dev builds only. */}
                      {import.meta.env.DEV && (
                        <Route path="/__first-hello-preview" element={<FirstHelloPreview />} />
                      )}
                      {/* CONNECT-2 — a page, not a tab. */}
                      <Route
                        path="/dashboard/connectors"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <ConnectorsPage />
                          </AuthGuard>
                        }
                      />
                      {/* ONE-GHOST-2 — retired. Everything it carried has a
                          home: the queue at /dashboard/approvals, the
                          briefing and the Bots in the panel. */}
                      <Route path="/dashboard/ghost" element={<RetiredGhostPageRedirect />} />
                      {/* The old path REDIRECTS PERMANENTLY and is never
                          removed: merchants have bookmarks, and every morning
                          briefing email already sent carries the old link. */}
                      <Route path="/dashboard/servicebot" element={<RetiredGhostPageRedirect />} />
                      {/* Phase 62.I — Ghost reactivation list. */}
                      <Route
                        path="/dashboard/reactivation"
                        element={
                          <AuthGuard requireCapability="leads.view">
                            <Reactivation />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/snapshots"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <PlaceholderPage
                              title="Snapshots"
                              sectionLabel="Settings"
                              sectionPath="/dashboard/settings"
                              description="Export your pipelines, plans, and pages as a portable backup. Snapshots are coming soon."
                            />
                          </AuthGuard>
                        }
                      />
                      {/* Wave N4a: the services price book manager. */}
                      <Route
                        path="/dashboard/services"
                        element={
                          <AuthGuard requireCapability="quotes.view">
                            <Services />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/plans"
                        element={
                          <AuthGuard requireCapability="service_plans.manage">
                            <Plans />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/jobs"
                        element={
                          <AuthGuard requireCapability="jobs.view">
                            <Jobs />
                          </AuthGuard>
                        }
                      />
                      {/* Wave N3c: job detail page. */}
                      <Route
                        path="/dashboard/jobs/:id"
                        element={
                          <AuthGuard requireCapability="jobs.view">
                            <JobDetail />
                          </AuthGuard>
                        }
                      />
                      {/* Phase F10.0.a: field operations dispatch board. */}
                      <Route
                        path="/dashboard/operations/dispatch"
                        element={
                          <AuthGuard requireCapability="dispatch.manage">
                            <Dispatch />
                          </AuthGuard>
                        }
                      />
                      {/* Phase F10.0.b: tech-side schedule view. */}
                      <Route
                        path="/dashboard/operations/tech-view"
                        element={
                          <AuthGuard requireCapability="schedule.view_own">
                            <TechView />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/clock"
                        element={
                          <AuthGuard requireCapability="time.clock_self">
                            <Clock />
                          </AuthGuard>
                        }
                      />
                      {/* Phase F: payroll surfaces. */}
                      <Route
                        path="/dashboard/payroll"
                        element={
                          <AuthGuard requireCapability="payroll.view_dashboard">
                            <Payroll />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/my-paychecks"
                        element={
                          <AuthGuard requireCapability="paychecks.view_own">
                            <MyPaychecks />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/pay-periods"
                        element={
                          <AuthGuard requireCapability="payroll.manage_periods">
                            <PayPeriodSettings />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/notifications"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <NotificationSettings />
                          </AuthGuard>
                        }
                      />
                      {/* Wave N4b: public quote settings. */}
                      <Route
                        path="/dashboard/settings/quote-page"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsQuotePage />
                          </AuthGuard>
                        }
                      />
                      {/* Wave P4: customer portal blocks. */}
                      <Route
                        path="/dashboard/settings/portal"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsPortalPage />
                          </AuthGuard>
                        }
                      />
                      {/* Wave F2: lead source vocabulary. */}
                      <Route
                        path="/dashboard/settings/lead-sources"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsLeadSourcesPage />
                          </AuthGuard>
                        }
                      />
                      {/* Wave F3: canvass disposition dictionary. */}
                      <Route
                        path="/dashboard/settings/canvass-dispositions"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsCanvassDispositionsPage />
                          </AuthGuard>
                        }
                      />
                      {/* Wave F4: merchant vocabulary (nouns + job status labels). */}
                      <Route
                        path="/dashboard/settings/vocabulary"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsVocabularyPage />
                          </AuthGuard>
                        }
                      />
                      {/* Wave F5: merchant message templates. */}
                      <Route
                        path="/dashboard/settings/messages"
                        element={
                          <AuthGuard requireCapability="settings.account_manage">
                            <SettingsMessagesPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/bonuses"
                        element={
                          <AuthGuard requireCapability="commissions.rules_manage">
                            <SettingsBonuses />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 73.4: affiliate payouts settings. Affiliates are
                merchants; payouts go to their Whop payment account. */}
                      <Route
                        path="/dashboard/settings/bank-account"
                        element={
                          <AuthGuard requireCapability="payment_account.view">
                            <SettingsBankAccount />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/customers"
                        element={
                          <AuthGuard requireCapability="customers.view">
                            <Customers />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/customers/import"
                        element={
                          <AuthGuard requireCapability="customers.import">
                            <CustomerImport />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/customers/:id"
                        element={
                          <AuthGuard requireCapability="customers.view">
                            <CustomerDetail />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/pipelines"
                        element={
                          <AuthGuard requireCapability="opportunities.view">
                            <Pipelines />
                          </AuthGuard>
                        }
                      />
                      {/* SALES-UI-1: the Sales analytics area. */}
                      <Route
                        path="/dashboard/sales/analytics"
                        element={
                          <AuthGuard requireCapability="opportunities.view">
                            <SalesAnalytics />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/pipelines/templates"
                        element={
                          <AuthGuard requireCapability="pipelines.manage">
                            <PipelineTemplateGallery />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/quotes"
                        element={
                          <AuthGuard requireCapability="quotes.view">
                            <Quotes />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/quotes/:id"
                        element={
                          <AuthGuard requireCapability="quotes.view">
                            <QuoteDetail />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/invoices"
                        element={
                          <AuthGuard requireCapability="invoices.view">
                            <Invoices />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/editor"
                        element={
                          <AuthGuard requireCapability="marketing.page_edit">
                            <Editor />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/analytics"
                        element={
                          <AuthGuard requireCapability="marketing.page_analytics">
                            <Analytics />
                          </AuthGuard>
                        }
                      />
                      {/* ANALYTICS-UX-1: standalone per-site analytics, opened
                          from a card on the analytics home. */}
                      <Route
                        path="/dashboard/analytics/:siteId"
                        element={
                          <AuthGuard requireCapability="marketing.page_analytics">
                            <SiteAnalytics />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/leads"
                        element={
                          <AuthGuard requireCapability="leads.view">
                            <Leads />
                          </AuthGuard>
                        }
                      />
                      {/* Canvass converged into the unified map: the old tab
                        redirects into canvass mode (bookmarks + muscle memory). */}
                      <Route
                        path="/dashboard/canvass"
                        element={<Navigate to="/dashboard/lead-map?mode=canvass" replace />}
                      />
                      <Route
                        path="/dashboard/lead-map"
                        element={
                          <AuthGuard requireCapability="canvass.use">
                            <LeadMapPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/leaderboard"
                        element={
                          <AuthGuard requireCapability="leaderboard.view">
                            <Leaderboard />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/leaderboard/tv"
                        element={
                          <AuthGuard requireCapability="leaderboard.view">
                            <LeaderboardTV />
                          </AuthGuard>
                        }
                      />
                      {/* Phase U12: unified inbox foundation. Channel
                send/receive paths ship in U13-U19. */}
                      <Route
                        path="/dashboard/inbox"
                        element={
                          <AuthGuard requireCapability="inbox.view">
                            <Inbox />
                          </AuthGuard>
                        }
                      />
                      {/* Phase U16: web chat widget settings. Configures the
                embeddable chat bubble + provides the merchant's
                embed snippet for their own website. */}
                      <Route
                        path="/dashboard/inbox/widget-settings"
                        element={
                          <AuthGuard requireCapability="inbox.channels_manage">
                            <InboxWidgetSettings />
                          </AuthGuard>
                        }
                      />
                      {/* Phase U13.1: per-sub_account branded email sending domain
                settings. Owner/admin manage Resend domain + DNS records
                here. */}
                      <Route
                        path="/dashboard/inbox/email-domain"
                        element={
                          <AuthGuard requireCapability="inbox.channels_manage">
                            <InboxEmailDomain />
                          </AuthGuard>
                        }
                      />
                      {/* Self-serve phone (step 2): per-sub_account number search
                + claim. Owner/admin sets up a dedicated business line. */}
                      <Route
                        path="/dashboard/inbox/phone-setup"
                        element={
                          <AuthGuard requireCapability="phone.setup_manage">
                            <InboxPhoneSetup />
                          </AuthGuard>
                        }
                      />
                      {/* BUILD-2 B2c: the standalone Calls page is retired —
                          calls are first-class events in the unified inbox
                          thread + the contact profile. Redirect (never 404) to
                          the voice-filtered inbox so bookmarks/links survive. */}
                      <Route
                        path="/dashboard/inbox/calls"
                        element={<Navigate to="/dashboard/inbox?channel=voice" replace />}
                      />
                      {/* Phase U17.2 + 56.5.1: WhatsApp inbox tab. Gated by
                VITE_FEATURE_WHATSAPP. */}
                      {features.whatsapp && (
                        <Route
                          path="/dashboard/inbox/whatsapp"
                          element={
                            <AuthGuard requireCapability="inbox.channels_manage">
                              <InboxWhatsApp />
                            </AuthGuard>
                          }
                        />
                      )}
                      {/* Phase U14.2 + 56.5.1: FB Messenger inbox tab. Gated by
                VITE_FEATURE_MESSENGER. */}
                      {features.messenger && (
                        <Route
                          path="/dashboard/inbox/messenger"
                          element={
                            <AuthGuard requireCapability="inbox.channels_manage">
                              <InboxMessenger />
                            </AuthGuard>
                          }
                        />
                      )}
                      {/* Phase U15 + 56.5.1: Instagram DM inbox tab. Gated by
                VITE_FEATURE_INSTAGRAM. */}
                      {features.instagram && (
                        <Route
                          path="/dashboard/inbox/instagram"
                          element={
                            <AuthGuard requireCapability="inbox.channels_manage">
                              <InboxInstagram />
                            </AuthGuard>
                          }
                        />
                      )}
                      {/* Wave 8.1: Lovable-style sites home replaces both the
                old SitesIndex and the 3-step NewSite stepper. The prompt
                input + project grid live on a single page. */}
                      {/* Launch: Sites is hidden and deferred to a future build.
                  While features.sites is off, every sites URL redirects to
                  the dashboard so a launch user never lands on a half-built
                  page. The components stay imported (and in the bundle) so
                  flipping VITE_FEATURE_SITES=true restores the feature with
                  no rebuild. */}
                      <Route
                        path="/dashboard/sites"
                        element={
                          features.sites ? (
                            <AuthGuard requireCapability="marketing.page_edit">
                              <SitesHome />
                            </AuthGuard>
                          ) : (
                            <Navigate to="/dashboard" replace />
                          )
                        }
                      />
                      <Route
                        path="/dashboard/sites/new"
                        element={
                          <Navigate
                            to={features.sites ? '/dashboard/sites' : '/dashboard'}
                            replace
                          />
                        }
                      />
                      <Route
                        path="/dashboard/sites/:project_id"
                        element={
                          features.sites ? (
                            <AuthGuard requireCapability="marketing.page_edit">
                              <SiteEditor />
                            </AuthGuard>
                          ) : (
                            <Navigate to="/dashboard" replace />
                          )
                        }
                      />

                      {/* THE-ROUTE-EXISTS-1: the section's own address. It
                          was declared in no route table, so the router's best
                          match for it was the PUBLIC SITE resolver at
                          /:slugOrUsername/:routePath. */}
                      <Route
                        path="/dashboard/marketing"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <MarketingSection />
                          </AuthGuard>
                        }
                      />
                      {/* MARKETING-RETURN-1: the Return page, front door of
                          Marketing. Gated by the money capability. */}
                      <Route
                        path="/dashboard/marketing/return"
                        element={
                          <AuthGuard requireCapability="money.section.view">
                            <MarketingReturn />
                          </AuthGuard>
                        }
                      />
                      {/* DASH-PRESETS-1: the retired Channels URLs. The index
                          lands on Home, where the switcher is; a channel URL
                          lands on that channel's TAB, so an old bookmark
                          opens the same dashboard it always did. */}
                      <Route
                        path="/dashboard/marketing/channels"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route
                        path="/dashboard/marketing/channels/:platform"
                        element={<RetiredChannelRedirect />}
                      />
                      {/* Phase 44: email campaign builder. */}
                      <Route
                        path="/dashboard/marketing/campaigns"
                        element={
                          <AuthGuard requireCapability="marketing.campaigns_manage">
                            <Campaigns />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 60.S2: demoted from admin — merchant-accessible. */}
                      <Route
                        path="/dashboard/marketing/automations"
                        element={
                          <AuthGuard requireCapability="automations.manage">
                            <AdminWorkflows />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/marketing/audiences"
                        element={
                          <AuthGuard requireCapability="marketing.campaigns_manage">
                            <AdminAudiences />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/reputation"
                        element={
                          <AuthGuard requireCapability="reviews.settings_manage">
                            <AdminReputationSettings />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/reviews"
                        element={
                          <AuthGuard requireCapability="reviews.triage">
                            <AdminInternalReviews />
                          </AuthGuard>
                        }
                      />
                      {features.whatsapp && (
                        <Route
                          path="/dashboard/settings/whatsapp"
                          element={
                            <AuthGuard requireCapability="settings.integrations_manage">
                              <AdminWhatsAppSettings />
                            </AuthGuard>
                          }
                        />
                      )}
                      {(features.messenger || features.instagram) && (
                        <Route
                          path="/dashboard/settings/integrations/meta"
                          element={
                            <AuthGuard requireCapability="settings.integrations_manage">
                              <AdminMetaConnections />
                            </AuthGuard>
                          }
                        />
                      )}
                      {/* CONNECT-2 W2: the Connections hub is the one home
                       * for every lead source connection. The old
                       * meta-leadgen and google-lead-ads pages redirect
                       * here (query params preserved so the OAuth
                       * callback's ?connected=/?error= still land). */}
                      <Route
                        path="/dashboard/settings/connections"
                        element={
                          <AuthGuard requireCapability="settings.integrations_manage">
                            <ConnectionsHub />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/integrations/meta-leadgen"
                        element={<RedirectPreservingSearch to="/dashboard/settings/connections" />}
                      />
                      <Route
                        path="/dashboard/settings/integrations/google-lead-ads"
                        element={<RedirectPreservingSearch to="/dashboard/settings/connections" />}
                      />
                      <Route
                        path="/dashboard/settings"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <Settings />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/payments"
                        element={
                          <AuthGuard requireCapability="payment_account.view">
                            <Payments />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/team"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <Team />
                          </AuthGuard>
                        }
                      />
                      {/* Phase D: per-team-member detail page with
                Profile + Compensation tabs. */}
                      <Route
                        path="/dashboard/settings/team/:memberId"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <TeamMember />
                          </AuthGuard>
                        }
                      />
                      {/* Phase O5a: VA Google Calendar OAuth + working
                hours editor. Self-serve and admin views share
                the CalendarSettings component. */}
                      <Route
                        path="/dashboard/account/calendar"
                        element={
                          <AuthGuard requireCapability="schedule.view_own">
                            <AccountCalendar />
                          </AuthGuard>
                        }
                      />
                      {/* THE-ROUTE-EXISTS-1: the section's own address, which
                          was declared nowhere. Not one of Team's six children
                          lives under it, so it redirects rather than landing. */}
                      <Route
                        path="/dashboard/team"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <TeamSection />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/team/:memberId/calendar"
                        element={
                          <AuthGuard requireCapability="calendar.manage">
                            <CalendarForMember />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/settings/commissions"
                        element={
                          <AuthGuard requireCapability="commissions.rules_manage">
                            <SettingsCommissions />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/my-commissions"
                        element={
                          <AuthGuard requireCapability="commissions.view_own">
                            <MyCommissions />
                          </AuthGuard>
                        }
                      />
                      {/* MONEY-IA-2: the merged Plan and usage page. */}
                      <Route
                        path="/dashboard/money/plan-and-usage"
                        element={
                          <AuthGuard requireCapability="billing.view">
                            <PlanAndUsage />
                          </AuthGuard>
                        }
                      />
                      {/* MONEY-IA-2: permanent, query-preserving redirects. The
                          subscribe return url (/billing/usage?subscribed=1) and
                          the top-up return url (/billing/wallet?topup=ok) land
                          here and their query strings survive the redirect. */}
                      <Route
                        path="/dashboard/billing/plan"
                        element={<RedirectPreservingSearch to="/dashboard/money/plan-and-usage" />}
                      />
                      <Route
                        path="/dashboard/billing/wallet"
                        element={<RedirectPreservingSearch to="/dashboard/money/plan-and-usage" />}
                      />
                      <Route
                        path="/dashboard/billing/usage"
                        element={<RedirectPreservingSearch to="/dashboard/money/plan-and-usage" />}
                      />
                      {/* MONEY-IA-2: Revenue moved under business analytics; the
                          old /dashboard/billing address redirects permanently. */}
                      <Route
                        path="/dashboard/analytics/revenue"
                        element={
                          <AuthGuard requireCapability="billing.view">
                            <Billing />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/billing"
                        element={<RedirectPreservingSearch to="/dashboard/analytics/revenue" />}
                      />
                      {/* Phase V1.6: Fees UI — volume-tier graduation surface +
                MTD progress + tier history + FAQ. MONEY-IA-2: gated to
                billing.view to match the What-you-pay menu. */}
                      <Route
                        path="/dashboard/billing/fees"
                        element={
                          <AuthGuard requireCapability="billing.view">
                            <FeesPage />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 39.2: affiliate PDF statements. Registered before
                /dashboard/affiliate so the literal segment wins. */}
                      <Route
                        path="/dashboard/affiliate/statements"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <AffiliateStatements />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/affiliate"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <AffiliateDashboard />
                          </AuthGuard>
                        }
                      />
                      {/* Phase 62.D.4: agency shell — clients list, per-client
                detail, and cross-client reporting. Only meaningful for
                users whose active sub_account has agency_account_id set;
                pages self-guard with an "agency only" message. */}
                      <Route
                        path="/dashboard/agency/clients"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <AgencyClients />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/agency/clients/:id"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <AgencyClientDetail />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/agency/reporting"
                        element={
                          <AuthGuard requireCapability="reports.team_view">
                            <AgencyReporting />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/pages"
                        element={
                          <AuthGuard requireCapability="team.view">
                            <Pages />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              {/* PLATFORM-TRUTH-1: the platform analytics page
                                  is retired. Every money figure on it duplicated
                                  Accounts and was computed from unbounded reads
                                  that stopped at 1,000 rows. The console home is
                                  now Accounts, which answers the same questions
                                  from paged, internal-excluded reads. */}
                              <Navigate to="/dashboard/admin/accounts" replace />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/onboarding-specialists"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminOnboardingSpecialists />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/health"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminHealth />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/accounts"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminAccounts />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/affiliates"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminAffiliates />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase R4.3c: per-affiliate detail page. Must be
                registered AFTER /dashboard/admin/affiliates so the
                literal segment wins for the list view; React Router
                v6 picks the most specific match regardless of order
                in practice, but keeping the literal first is the
                pattern the codebase follows (see /onboarding-queue
                children above). */}
                      <Route
                        path="/dashboard/admin/affiliates/:id"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminAffiliateDetail />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/affiliate-clawbacks"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminAffiliateClawbacks />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase O6: nurture sequence admin (platform admin only). */}
                      <Route
                        path="/dashboard/admin/nurture-sequences"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminNurtureSequences />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/affiliate-events"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminAffiliateEvents />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/bonuses"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminBonuses />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/admin/ai-pricing"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AiPricing />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase 62.C.2.10: admin cost dictionary CRUD. */}
                      <Route
                        path="/dashboard/admin/cost-dictionary"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminCostDictionary />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase 60.S2: redirects from old admin routes. */}
                      <Route
                        path="/dashboard/admin/onboarding-analytics"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <OnboardingAnalytics />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase 62.E.6: activation funnel admin dashboard. */}
                      <Route
                        path="/dashboard/admin/activation-funnel"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <ActivationFunnel />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      {/* Phase 60.S2: redirects from old admin routes (30-day deprecation). */}
                      <Route
                        path="/dashboard/admin/workflows"
                        element={<Navigate to="/dashboard/marketing/automations" replace />}
                      />
                      <Route
                        path="/dashboard/admin/audiences"
                        element={<Navigate to="/dashboard/marketing/audiences" replace />}
                      />
                      {/* Phase 40: demo accounts admin. */}
                      <Route
                        path="/dashboard/admin/demo-accounts"
                        element={
                          <AuthGuard>
                            <AdminGuard>
                              <AdminDemoAccounts />
                            </AdminGuard>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <AiStudio />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai/team-photos"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <TeamPhotos />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai/cover"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <CoverPhoto />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai/logo"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <Logo />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai/writer"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <Writer />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard/ai/history"
                        element={
                          <AuthGuard requireCapability="ai.tools_use">
                            <History />
                          </AuthGuard>
                        }
                      />

                      <Route path="/q/:public_token" element={<PublicQuote />} />

                      <Route path="/i/:public_token" element={<PublicInvoice />} />

                      <Route path="/p/:slug" element={<PublicServicePage />} />

                      {/* Wave 8.2: iframe target for the editor's live preview.
                Renders the bare site (no dashboard chrome). RLS on
                sites + site_pages gates access by team_members
                membership — same-origin iframe shares localStorage
                auth, so a non-team-member iframe URL renders nothing. */}
                      {/* Launch: gated with the rest of Sites. The preview is
                  only reached from the (now hidden) editor iframe; redirect
                  to the dashboard while features.sites is off. */}
                      <Route
                        path="/sites/:siteId/preview"
                        element={
                          features.sites ? <SitePreview /> : <Navigate to="/dashboard" replace />
                        }
                      />

                      {/* Phase O5c: tokenized customer-facing flows for
                onboarding-call lifecycle. Public, OUTSIDE AuthGuard,
                no Layout. Token-gated via SECURITY DEFINER RPCs. */}
                      <Route path="/reschedule/:token" element={<RescheduleAppointment />} />
                      <Route path="/cancel/:token" element={<CancelAppointment />} />

                      {/* Phase 47: public review landing — anon, token-gated. */}
                      <Route path="/r/:token" element={<ReviewLanding />} />

                      {/* Wave 13: customer portal — anon, token-gated. The
                    token IS the auth; portal-fetch edge function
                    validates server-side and returns scoped data. */}
                      <Route path="/portal/:token" element={<CustomerPortal />} />

                      {/* Phase U16: web chat panel rendered inside the launcher
                iframe. NO Layout, NO AuthGuard — full-bleed minimal
                visitor surface. Registered BEFORE /:username so the
                literal /widget/ segment wins. */}
                      <Route path="/widget/:sub_slug" element={<Widget />} />

                      {/* Phase 62.A.S4: unified resolver — tries project slug first,
                falls back to legacy profile username. Two-segment route
                handles project sub-pages (e.g. /my-site/about). */}
                      {/* THE-DOOR-TO-THE-BUILD-1 — AND ONLY WHEN THE FIRST
                SEGMENT IS NOT THE PRODUCT'S OWN. React Router ranks a two
                segment parameterised route above the wildcard, so every two
                segment address the app does not register came here and had its
                first segment looked up as a business: a merchant trimming
                /dashboard/marketing was shown the public site's miss page for a
                business called dashboard. `PublicAddress` sends those to the
                product's 404 instead and hands everything else straight to the
                resolver, unchanged. */}
                      <Route
                        path="/:slugOrUsername/:routePath"
                        element={
                          <PublicAddress>
                            <UnifiedPublicResolver />
                          </PublicAddress>
                        }
                      />
                      <Route
                        path="/:slugOrUsername"
                        element={
                          <PublicAddress>
                            <UnifiedPublicResolver />
                          </PublicAddress>
                        }
                      />

                      {/* Phase 56.5: catch-all 404. Matched only when no other
                route (including /:slugOrUsername for single-segment paths)
                ranks higher. React Router v6 uses ranking-based
                matching, so the wildcard naturally falls through. */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  {/* POLISH-1: the Ghost launcher moved into the
                  sidebar (see ServiceBotDock). It is no longer mounted
                  globally, because it is no longer an overlay. */}
                </VoiceDialerProvider>
              </AiJobsProvider>
            </AuthProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

// Phase 57.2: DashboardRootRedirect deleted — /dashboard now renders
// <Home /> directly. The Phase A2 first-visible-section redirect is
// preserved in the git history (last on main at aec46e8) if Home ever
// needs to fall back to per-role default routing.

// Shown for the instant a lazy route chunk loads.
/**
 * CAP-SPIKE-5 — what "/" means depends on where you are.
 *
 * ON THE WEB this is the marketing site, unchanged: LandingPage renders
 * exactly as it always has.
 *
 * IN THE SHELL a merchant who opened Ghost CRM wants Ghost CRM, not a pitch
 * for it. Signed out goes to login; signed in goes to the dashboard.
 *
 * THIS IS A ROUTING DECISION, NOT A REDIRECT AFTER PAINT. LandingPage is
 * never returned on native, so it never mounts, never fetches and never
 * paints. A redirect fired from inside LandingPage would flash the sales page
 * first, which is the same bug wearing a hat.
 */
function RootRoute() {
  // Hooks first, unconditionally: the branch below must never change the
  // order or count of hooks between renders.
  const { user, loading } = useAuth()
  if (!isNativeShell()) return <FrontDoor />
  if (loading) return <RouteLoading />
  return <Navigate to={user ? '/dashboard' : '/login'} replace />
}

/**
 * CAP-SPIKE-5 — a route that exists to SELL the product, not to run it.
 *
 * In the shell these resolve to the app instead of rendering. Public CUSTOMER
 * routes are deliberately NOT wrapped: a quote, invoice, portal, review,
 * reschedule or cancel link must still open if a merchant taps one, because
 * those are legitimate destinations. They just are not where the app starts.
 */
function WebOnlyRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (!isNativeShell()) return <>{children}</>
  if (loading) return <RouteLoading />
  return <Navigate to={user ? '/dashboard' : '/login'} replace />
}

function RouteLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-blue-600" />
    </div>
  )
}
